/**
 * SignaturePad - Komponen tanda tangan dengan canvas
 * Mendukung mouse (desktop) dan touch (Android/tablet)
 * Output: PNG transparan (trimmed - hanya area tanda tangan)
 */

import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import SignatureCanvas from "react-signature-canvas";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

/**
 * Trim canvas ke area yang berisi piksel non-transparan
 * @param {HTMLCanvasElement} canvas
 * @param {number} pad - padding piksel di sekeliling
 * @returns {HTMLCanvasElement} canvas baru yang sudah di-crop
 */
function trimCanvasToContent(canvas, pad = 4) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  const width = canvas.width;
  const height = canvas.height;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const alpha = data[i + 3];
      if (alpha > 10) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (minX > maxX || minY > maxY) return canvas;

  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width, maxX + pad + 1);
  maxY = Math.min(height, maxY + pad + 1);

  const cropWidth = maxX - minX;
  const cropHeight = maxY - minY;
  if (cropWidth <= 0 || cropHeight <= 0) return canvas;

  const trimmed = document.createElement("canvas");
  trimmed.width = cropWidth;
  trimmed.height = cropHeight;
  const tctx = trimmed.getContext("2d");
  tctx.drawImage(canvas, minX, minY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  return trimmed;
}

/**
 * @param {Object} props
 * @param {boolean} [props.disabled] - Nonaktifkan canvas dan tombol
 * @param {function} [props.onChange] - Callback saat tanda tangan berubah (isEmpty)
 * @param {number} [props.width] - Lebar maksimal canvas (px), digunakan jika ukuran tidak terukur
 * @param {number} [props.height] - Tinggi canvas (px)
 */
const SignaturePad = forwardRef(({ disabled = false, onChange, width = 400, height = 200 }, ref) => {
  const sigPadRef = useRef(null);
  const containerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width, height });

  // Ukur container untuk canvas full-width, koordinat tetap benar
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateSize = () => {
      if (el.offsetWidth > 0) {
        setCanvasSize({ width: el.offsetWidth, height });
      }
    };

    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [height]);

  useImperativeHandle(ref, () => ({
    /** Cek apakah canvas kosong */
    isEmpty: () => {
      if (!sigPadRef.current) return true;
      return sigPadRef.current.isEmpty();
    },
    /** Bersihkan canvas */
    clear: () => {
      if (sigPadRef.current) {
        sigPadRef.current.clear();
        setIsEmpty(true);
        if (onChange) onChange(true);
      }
    },
    /**
     * Ekspor tanda tangan sebagai base64 PNG (trimmed, transparan)
     * @returns {string|null} data:image/png;base64,... atau null jika kosong
     */
    getSignatureDataUrl: () => {
      if (!sigPadRef.current) return null;
      if (sigPadRef.current.isEmpty()) return null;
      try {
        const canvas = sigPadRef.current.getCanvas ? sigPadRef.current.getCanvas() : null;
        if (!canvas) return null;
        const trimmed = trimCanvasToContent(canvas);
        return trimmed.toDataURL("image/png");
      } catch (e) {
        try {
          return sigPadRef.current.toDataURL("image/png");
        } catch (e2) {
          console.error("Error exporting signature:", e2);
          return null;
        }
      }
    },
  }));

  // Defer setState agar tidak mengganggu event pointer saat menggambar
  const handleBegin = () => {
    requestAnimationFrame(() => {
      setIsEmpty(false);
      if (onChange) onChange(false);
    });
  };

  const handleEnd = () => {
    requestAnimationFrame(() => {
      if (sigPadRef.current && sigPadRef.current.isEmpty()) {
        setIsEmpty(true);
        if (onChange) onChange(true);
      }
    });
  };

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setIsEmpty(true);
      if (onChange) onChange(true);
    }
  };

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
        Tanda Tangan *
      </MDTypography>
      <MDBox
        ref={containerRef}
        sx={{
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 1,
          backgroundColor: "#f8f9fa",
          position: "relative",
          width: "100%",
          height: height,
        }}
      >
        <SignatureCanvas
          ref={sigPadRef}
          canvasProps={{
            width: canvasSize.width,
            height: canvasSize.height,
            style: {
              display: "block",
              width: canvasSize.width + "px",
              height: canvasSize.height + "px",
              touchAction: "none",
              userSelect: "none",
              backgroundColor: "transparent",
              pointerEvents: disabled ? "none" : "auto",
              opacity: disabled ? 0.6 : 1,
            },
          }}
          backgroundColor="rgba(0,0,0,0)"
          penColor="#000000"
          minDistance={1}
          throttle={8}
          onBegin={handleBegin}
          onEnd={handleEnd}
          clearOnResize={false}
        />
      </MDBox>
      <MDBox display="flex" gap={1} mt={1}>
        <MDButton
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handleClear}
          disabled={disabled}
        >
          Hapus Tanda Tangan
        </MDButton>
      </MDBox>
      {!isEmpty && (
        <MDTypography variant="caption" color="success" mt={0.5} display="block">
          Tanda tangan telah diisi
        </MDTypography>
      )}
    </MDBox>
  );
});

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
